// src/pages/autopilot/SearchProgress.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSearchQueries,
  setSearchResults,
  setVerifiedProspects,
  setLoading,
  setError,
  updateSearchProgress,
  goToPreviousStep,
  goToNextStep
} from '../../store/slices/autoPilotSlice';
import {
  generateSearchQueries,
  executeConcurrentSearches,
  filterResults,
  enrichProspects,
  verifyProspects
} from '../../services/autoPilotService';
import SearchQueryCard from '../../components/autopilot/SearchQueryCard';

function SearchProgress({ onNext, onBack }) {
  const dispatch = useDispatch();
  const {
    validatedICP,
    searchQueries,
    searchResults,
    loading,
    error,
    searchProgress
  } = useSelector((state) => state.autoPilot);

  const [queriesGenerated, setQueriesGenerated] = useState(false);
  const [searchesCompleted, setSearchesCompleted] = useState(false);
  const [filteringCompleted, setFilteringCompleted] = useState(false);
  const [enrichmentCompleted, setEnrichmentCompleted] = useState(false);

  useEffect(() => {
    if (validatedICP && !queriesGenerated && searchQueries.length === 0) {
      generateQueries();
    }
  }, [validatedICP]);

  useEffect(() => {
    if (queriesGenerated && searchQueries.length > 0 && !searchesCompleted) {
      executeSearches();
    }
  }, [queriesGenerated, searchQueries]);

  useEffect(() => {
    if (searchesCompleted && searchResults.length > 0 && !filteringCompleted) {
      filterSearchResults();
    }
  }, [searchesCompleted, searchResults]);

  useEffect(() => {
    if (filteringCompleted && !enrichmentCompleted) {
      enrichAndVerify();
    }
  }, [filteringCompleted]);

  const generateQueries = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await generateSearchQueries(validatedICP);
      if (response.status === 'success' && response.data) {
        dispatch(setSearchQueries(response.data));
        setQueriesGenerated(true);
      } else {
        dispatch(setError(response.message || 'Failed to generate search queries.'));
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Failed to generate search queries.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const executeSearches = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await executeConcurrentSearches(searchQueries);
      if (response.status === 'success' && response.data) {
        dispatch(setSearchResults(response.data));
        setSearchesCompleted(true);
      } else {
        dispatch(setError(response.message || 'Failed to execute searches.'));
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Failed to execute searches.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const filterSearchResults = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await filterResults(searchResults, validatedICP);
      if (response.status === 'success' && response.data) {
        dispatch(setSearchResults(response.data));
        setFilteringCompleted(true);
      } else {
        dispatch(setError(response.message || 'Failed to filter results.'));
        setFilteringCompleted(true); // Continue even if filtering fails
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Failed to filter results.'));
      setFilteringCompleted(true); // Continue even if filtering fails
    } finally {
      dispatch(setLoading(false));
    }
  };

  const enrichAndVerify = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const prospectIds = searchResults.slice(0, 20).map(p => p._id || p.id);
      if (prospectIds.length === 0) {
        setEnrichmentCompleted(true);
        return;
      }

      const enrichResponse = await enrichProspects(prospectIds);
      if (enrichResponse.status === 'success' && enrichResponse.data) {
        const verifyResponse = await verifyProspects(enrichResponse.data, validatedICP);
        if (verifyResponse.status === 'success' && verifyResponse.data) {
          const verifiedProspects = verifyResponse.data.slice(0, 20);
          dispatch(setSearchResults(verifiedProspects));
          dispatch(setVerifiedProspects(verifiedProspects));
          setEnrichmentCompleted(true);
        } else {
          // Use enriched data even if verification fails
          const prospects = enrichResponse.data.slice(0, 20);
          dispatch(setVerifiedProspects(prospects));
          setEnrichmentCompleted(true);
        }
      } else {
        // Use filtered results even if enrichment fails
        const prospects = searchResults.slice(0, 20);
        dispatch(setVerifiedProspects(prospects));
        setEnrichmentCompleted(true);
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Enrichment/verification failed.'));
      setEnrichmentCompleted(true); // Continue even if enrichment fails
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleContinue = () => {
    if (onNext) {
      onNext();
    } else {
      dispatch(goToNextStep());
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dispatch(goToPreviousStep());
    }
  };

  const allCompleted = enrichmentCompleted;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Search Progress
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
        We're generating search queries and executing them concurrently. This may take a few moments.
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      {/* Progress Overview */}
      <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Progress Overview
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Generate Search Queries</Typography>
            {queriesGenerated ? (
              <Typography variant="body2" sx={{ color: '#1BE885', fontWeight: 600 }}>
                ✓ Completed
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Execute Concurrent Searches</Typography>
            {searchesCompleted ? (
              <Typography variant="body2" sx={{ color: '#1BE885', fontWeight: 600 }}>
                ✓ Completed
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Filter Results</Typography>
            {filteringCompleted ? (
              <Typography variant="body2" sx={{ color: '#1BE885', fontWeight: 600 }}>
                ✓ Completed
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Enrich & Verify Prospects</Typography>
            {enrichmentCompleted ? (
              <Typography variant="body2" sx={{ color: '#1BE885', fontWeight: 600 }}>
                ✓ Completed
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Search Queries */}
      {searchQueries.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Generated Search Queries ({searchQueries.length})
          </Typography>
          {searchQueries.map((query, index) => {
            let status = 'pending';
            if (searchesCompleted) status = 'completed';
            else if (loading && index < searchProgress.completedQueries) status = 'running';

            return (
              <SearchQueryCard
                key={index}
                query={query}
                index={index}
                status={status}
                resultCount={searchProgress.resultsPerQuery[query.id] || 0}
              />
            );
          })}
        </Box>
      )}

      {loading && !allCompleted && (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <CircularProgress sx={{ color: '#1BE885' }} />
          <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
            Processing searches... This may take a few moments.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          disabled={loading}
          sx={{
            borderColor: '#666',
            color: '#666',
            '&:hover': { borderColor: '#333', bgcolor: '#f5f5f5' }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!allCompleted || loading}
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: '#1BE885',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#20ec84' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          Continue to Results
        </Button>
      </Box>
    </Box>
  );
}

export default SearchProgress;
